import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Camera, Search, Users, Building } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import DatePicker from '../components/DatePicker'
import { topService } from '../services/topService'
import { useApiError } from '../../shared'
import { MessageDialog } from '../../shared/ui'
import toast from 'react-hot-toast'

const Top = () => {
  const navigate = useNavigate()

  // State for counts
  const [satsueiCount, setSatsueiCount] = useState(0)

  // State for dates
  const [satsueiDate, setSatsueiDate] = useState(new Date().toISOString().split('T')[0])

  // State for detailed reports
  const [reportType, setReportType] = useState('user') // 'user' or 'branch'
  const [reportData, setReportData] = useState([])
  const [reportDataBranch, setReportDataBranch] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  // Use the error handling hook
  const {
    showErrorDialog,
    errorMessage,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false))

  const menuItems = [
    {
      id: 'satsueiDataToroku',
      title: '撮影データ登録',
      description: '新規で画像・撮影ラベルの登録、またはIDを指定して登録済の情報の更新を行います。',
      icon: Camera,
      path: '/satsueiDataToroku',
      color: 'bg-blue-500'
    },
    {
      id: 'satsueiDataKensaku',
      title: '撮影データ検索一覧',
      description: '登録した画像・撮影ラベルデータから様々な条件で検索し、結果を一覧表示します。',
      icon: Search,
      path: '/satsueiDataKensaku',
      color: 'bg-green-500'
    }
  ]

  // Define functions with useCallback to prevent infinite loops
  const loadSatsueiDayCount = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await topService.getObjectDayCount(satsueiDate)
      if (response.returnCode === '200') {
        setSatsueiCount(response.objectDayCount || 0)
        setReportData(response.objectDayUserDatas || [])
        setReportDataBranch(response.objectDayKyotenDatas || [])
      }
    } catch (error) {
      console.error('Error loading satsuei count:', error)
      toast.error('撮影データ件数の取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [satsueiDate])

  // Load initial data and load satsuei count when date changes
  useEffect(() => {
    loadSatsueiDayCount()
  }, [loadSatsueiDayCount])

  const handleMenuClick = (path) => {
    navigate(path)
  }

  const handleSatsueiReportClick = () => {
    navigate('/satsueiDataKensaku', {
      state: {
        satsueiDate: satsueiDate
      }
    })
  }

  const handleReportItemClick = (item) => {
    // Navigate to satsuei search with filters
    navigate('/satsueiDataKensaku', {
      state: {
        satsueiDate: satsueiDate,
        userId: reportType === 'user' ? item.pixUserId : undefined,
        kyotenId: reportType === 'branch' 
        ? (item.kyotenId === null ? 0 : item.kyotenId !== "" ? item.kyotenId : undefined)
        : undefined
      }
    })
  }

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short"
    })
  }

  return (
    <Layout pageTitle="トップページ">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left Side - Menu Buttons */}
        <div className="w-full lg:w-1/2 order-1">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 lg:space-y-6"
          >
            {/* 撮影管理エリア */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="bg-gray-100 px-4 py-2 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">撮影管理</h2>
              </div>
              <div className="p-4 lg:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 lg:gap-4">
                  {menuItems.slice(0, 2).map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="top-menu-item rounded-lg hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      onClick={() => handleMenuClick(item.path)}
                    >
                      <div className="block p-3 lg:p-4">
                        <div className="flex items-start space-x-3 lg:space-x-4">
                          <div className={`p-2 lg:p-3 rounded-lg ${item.color} flex-shrink-0`}>
                            <item.icon className="h-6 w-6 lg:h-8 lg:w-8 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xs lg:text-sm font-bold text-gray-900 mb-1 lg:mb-2 bg-white border border-gray-300 rounded-lg px-2 py-1">
                              {item.title}
                            </h3>
                            <p className="text-xs text-gray-600 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Side - Summary Panels */}
        <div className="w-full lg:w-1/2 order-2">
          <div className="space-y-4 lg:space-y-6">
            {/* 撮影データ登録件数 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="top-report-panel rounded-lg shadow-sm"
            >
              <div className="p-4">
                <div className="mb-3">
                  <DatePicker
                    value={satsueiDate}
                    onChange={setSatsueiDate}
                    className="w-full"
                  />
                </div>
                <div className="text-sm font-semibold text-gray-800 mb-4">撮影データ登録件数</div>
                <div className="flex items-end mb-4">
                  <div className="text-2xl lg:text-3xl font-bold text-gray-900">
                    {isLoading ? '...' : satsueiCount}
                  </div>
                  <div className="text-sm text-gray-600 ml-1">件</div>
                </div>
                <button
                  onClick={handleSatsueiReportClick}
                  className="top-report-button w-full text-white text-xs py-2 px-4"
                >
                  一覧で確認する
                </button>

                {/* Report Type Buttons */}
                <div className="flex mt-4 space-x-2">
                  <button
                    onClick={() => setReportType('user')}
                    className={`flex-1 border border-gray-300 rounded p-2 text-center cursor-pointer transition-colors ${reportType === 'user' ? 'bg-red-100 border-red-300' : 'hover:bg-gray-50'
                      }`}
                  >
                    <Users className="h-4 w-4 mx-auto mb-1 text-gray-600" />
                    <div className="text-xs text-gray-600">担当者別</div>
                  </button>
                  <button
                    onClick={() => setReportType('branch')}
                    className={`flex-1 border border-gray-300 rounded p-2 text-center cursor-pointer transition-colors ${reportType === 'branch' ? 'bg-red-100 border-red-300' : 'hover:bg-gray-50'
                      }`}
                  >
                    <Building className="h-4 w-4 mx-auto mb-1 text-gray-600" />
                    <div className="text-xs text-gray-600">拠点別</div>
                  </button>
                </div>

                {/* Detailed Report List */}
                <div className="mt-4 max-h-32 lg:max-h-48 overflow-y-auto">
                  {isLoading ? (
                    <div className="text-center text-gray-500 text-sm">読み込み中...</div>
                  ) : (
                    (() => {
                      const dataList = reportType === 'user' ? reportData : reportDataBranch
                      if (!dataList || dataList.length === 0) {
                        return <div className="text-center text-gray-500 text-sm">データがありません</div>
                      }

                      return (
                        <ul className="space-y-2">
                          {dataList.map((item, index) => (
                            <li
                              key={index}
                              onClick={() => handleReportItemClick(item)}
                              className="flex justify-between items-center p-2 hover:bg-gray-50 cursor-pointer rounded text-sm border-b border-gray-100"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-500">{formatDateForDisplay(satsueiDate)}</div>
                                <div className="font-medium text-gray-800 truncate">
                                  {reportType === 'user' ? item.userName : (item.kyotenName || '未指定')}
                                </div>
                              </div>
                              <div className="text-sm font-semibold text-gray-600 flex-shrink-0 ml-2">
                                {reportType === 'user' ? item.userObjectCount : item.kyotenObjectCount}枚
                              </div>
                            </li>
                          ))}
                        </ul>
                      )
                    })()
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Error Dialog */}
      <MessageDialog
          isOpen={showErrorDialog}
          onClose={closeErrorDialog}
          title="メッセージ"
          message={errorMessage}
          type="error"
        />
    </Layout>
  )
}

export default Top
